import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Circle as XCircle, Info } from 'lucide-react';
import InputMask from 'react-input-mask-next';
import { supabase } from '../lib/supabase';
import LeadSuccessScreen from '../components/LeadSuccessScreen';

type PageState = 'loading' | 'form' | 'submitting' | 'success' | 'duplicate' | 'rejected' | 'not_found';

export default function LeadCapture() {
  const { slug } = useParams<{ slug: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submittingLock = useRef(false);

  useEffect(() => {
    if (!slug) { setPageState('not_found'); return; }

    (async () => {
      const { data } = await supabase
        .from('unit_lead_config')
        .select('unit_id, is_active')
        .eq('slug', slug)
        .maybeSingle();

      if (!data || !data.is_active) {
        setPageState('not_found');
        return;
      }

      setUnitId(data.unit_id);
      setPageState('form');

      supabase.rpc('increment_lead_page_access', { p_slug: slug });
    })();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingLock.current) return;

    const cpfDigits = cpf.replace(/\D/g, '');
    const phoneDigits = phone.replace(/\D/g, '');

    if (!name.trim()) { setError('Informe seu nome completo.'); return; }
    if (cpfDigits.length !== 11) { setError('CPF invalido.'); return; }
    if (phoneDigits.length < 10) { setError('Telefone invalido.'); return; }
    if (!unitId) { setError('Erro interno. Tente novamente.'); return; }

    submittingLock.current = true;
    setError(null);
    setPageState('submitting');

    try {
      const { data, error: rpcError } = await supabase.rpc('submit_lead', {
        p_unit_id: unitId,
        p_cpf: cpfDigits,
        p_name: name.trim(),
        p_phone: phoneDigits,
      });

      if (rpcError) throw rpcError;

      const result = data?.[0];
      if (result?.lead_status === 'rejected_auto') {
        setPageState('rejected');
      } else if (result?.lead_status === 'duplicate') {
        setPageState('duplicate');
      } else {
        setPageState('success');
      }
    } catch (err) {
      console.error('Error submitting lead:', err);
      setError('Erro ao enviar cadastro. Tente novamente.');
      setPageState('form');
      submittingLock.current = false;
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (pageState === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Pagina nao encontrada.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return <LeadSuccessScreen name={name} phone={phone} />;
  }

  if (pageState === 'duplicate') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Info className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Voce ja possui cadastro</h2>
          <p className="text-gray-600">Ja recebemos seus dados anteriormente. Uma atendente entrara em contato pelo seu WhatsApp, nao e necessario enviar novamente.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cadastro nao aprovado</h2>
          <p className="text-gray-600">Infelizmente nao foi possivel prosseguir com seu cadastro no momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center">Cadastro Rapido</h1>
        <p className="text-gray-500 text-sm text-center mb-6">Preencha seus dados para continuar</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Seu nome completo"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              disabled={pageState === 'submitting'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
            <InputMask
              mask="999.999.999-99"
              value={cpf}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
              placeholder="000.000.000-00"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              disabled={pageState === 'submitting'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
            <InputMask
              mask="(99) 99999-9999"
              value={phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              disabled={pageState === 'submitting'}
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={pageState === 'submitting'}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {pageState === 'submitting' ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Enviando...
              </>
            ) : (
              'Continuar'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
